<template>
    <div class="flex mb-2 justify-content-end">
        <div class="dash-toolbar">
            <div class="date-picker-wrap dp-input-wrapper">
                <span class="dp-cal-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                </span>
                <DatePicker v-model="selectedMonth" view="month" dateFormat="mm/yy" placeholder="Selecionar mês" @date-select="onMonthChange" />
            </div>
        </div>
    </div>

    <div class="flex gap-2 pt-2.5">
        <Card class="flex-1" style="text-align: center">
            <template #content>
                <div class="font-bold text-2xl">R$ {{ formatMoney(financeiroInfos.total) }}</div>
                <div class="font-medium text-blue-500"><b>Total de Contas</b></div>
            </template>
        </Card>

        <Card class="flex-1" style="text-align: center">
            <template #content>
                <div class="font-bold text-2xl">R$ {{ formatMoney(financeiroInfos.pago) }}</div>
                <div class="font-medium text-green-500"><b>Total Pago</b></div>
            </template>
        </Card>

        <Card class="flex-1" style="text-align: center">
            <template #content>
                <div class="font-bold text-2xl">R$ {{ formatMoney(financeiroInfos.pendente) }}</div>
                <div class="font-medium text-red-500"><b>Total Pendente</b></div>
            </template>
        </Card>
    </div>

    <div class="flex gap-2 my-2">
        <Card class="flex-1" style="min-height: 400px;"> <template #title>
                <h4 class="m-0">PAGO vs PENDENTE (Mês Selecionado)</h4>
            </template>
            <template #content>
                <div style="max-width: 300px; margin: auto;"> <Chart v-if="_isMounted && paidPendingChartData?.labels?.length && paidPendingChartData?.datasets?.length" :key="`paid-${selectedMonth?.getTime?.() || 0}`" type="doughnut" :data="paidPendingChartData" :options="chartOptions" />
                </div>
                <div v-if="isLoading.donut" class="chart-loader">
                    <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="6" />
                    <span>CARREGANDO GRÁFICO...</span>
                </div>
            </template>
        </Card>

        <Card class="flex-1" style="min-height: 400px;"> <template #title>
                <h4 class="m-0">COMPARATIVO MENSAL</h4>
            </template>
            <template #content>
                <Chart v-if="_isMounted && monthlyFinancialChartData?.labels?.length && monthlyFinancialChartData?.datasets?.length" :key="`monthly-${selectedMonth?.getTime?.() || 0}`" type="bar" :data="monthlyFinancialChartData" :options="chartOptionsMultiAxis" />
                <div v-if="isLoading.bar" class="chart-loader">
                    <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="6" />
                    <span>CARREGANDO GRÁFICO...</span>
                </div>
            </template>
        </Card>
    </div>
</template>

<script>
import DatePicker from 'primevue/datepicker';
import SafeChart from '@/components/SafeChart.vue';
import Card from 'primevue/card';
import ProgressSpinner from 'primevue/progressspinner';

// Assumindo que você terá um serviço para as chamadas de API financeiras
import Conta from '@/service/conta.js';

import Mask from '@/utils/Mask.js';

export default {
    components: {
        DatePicker,
        Chart: SafeChart,
        Card,
        ProgressSpinner,
    },
    data() {
        return {
            isLoading: { donut: false, bar: false },
            selectedMonth: new Date(), // Data inicial para o filtro, default para o mês atual
            financeiroInfos: {
                total: 0,
                pago: 0,
                pendente: 0,
            },
            paidPendingChartData: {}, // Dados para o gráfico de donut
            monthlyFinancialChartData: {}, // Dados para o gráfico de barra
            chartOptions: {
                responsive: true,
                plugins: {
                    legend: {
                        position: 'top',
                    },
                    tooltip: { // Adicione ou modifique a configuração do tooltip aqui
                        callbacks: {
                            label: function(context) {
                                let label = context.label || ''; // context.label é o rótulo da fatia (ex: 'Total Pago')

                                if (label) {
                                    label += ': ';
                                }
                                if (context.parsed !== null) { // Para donut, context.parsed é o valor numérico
                                    label += 'R$ ' + context.parsed.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                }
                                return label;
                            }
                        }
                    }
                },
            },
            chartOptionsMultiAxis: {
                responsive: true,
                plugins: {
                    legend: {
                        position: 'top',
                    },
                    tooltip: {
                        callbacks: {
                            label: function(context) {
                                let label = context.dataset.label || '';

                                if (label) {
                                    label += ': ';
                                }
                                if (context.parsed.y !== null) {
                                    label += 'R$ ' + context.parsed.y.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                }
                                return label;
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        stacked: false, // Mantém false para barras não empilhadas
                    },
                    y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Valor (R$)' // Título do eixo Y continua "Valor (R$)"
                        },
                    },
                }
            },

            _isMounted: false,
        };
    },
    mounted() {
        this._isMounted = true;
        this.onMonthChange(); // Carrega os dados iniciais ao montar o componente
    },
    beforeUnmount() {
        this._isMounted = false;
    },
    methods: {
        // Função chamada ao mudar o mês no DatePicker
        onMonthChange() {
            this.loadFinanceiroData();
            this.loadMonthlyFinancialChart();
        },

        // Carrega os totais de contas, pago e pendente para o mês selecionado
        async loadFinanceiroData() {
            this.isLoading.donut = true;
            try {
                const params = [`data=${this.selectedMonth.toISOString().split('T')[0]}`];

                const response = await Conta.valorTotal(params.join("&"));
                if (!this._isMounted) return;
                const data = response.data;

                this.financeiroInfos.total      = data.total;
                this.financeiroInfos.pago       = data.pago;
                this.financeiroInfos.pendente   = data.pendente;

                this.loadPaidPendingChart(data);

            } catch (error) {
                console.error("Erro ao carregar totais financeiros:", error);
                this.$toast.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar Totais', life: 5000 });
            } finally {
                this.isLoading.donut = false;
            }
        },

        // Carrega os dados para o gráfico de donut (Total Pago x Total Pendente)
        async loadPaidPendingChart(data) {
            try {
                if (!this._isMounted) return;
                this.paidPendingChartData = {
                    labels: ['Total Pago', 'Total Pendente'],
                    datasets: [
                        {
                            data: [data.pago, data.pendente],
                            backgroundColor: ['#22C55E', '#EF4444'], // Verde para pago, Vermelho para pendente
                            hoverBackgroundColor: ['#16A34A', '#DC2626']
                        }
                    ]
                };
            } catch (error) {
                console.error("Erro ao carregar dados do gráfico de pago/pendente:", error);
                this.$toast.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar Gráfico', life: 5000 });
            }
        },

        // Carrega os dados para o gráfico de barra (Total Pago últimos 3 meses e Total Contas mensal)
        async loadMonthlyFinancialChart() {
            this.isLoading.bar = true;
            try {
                const params = [`data=${this.selectedMonth.toISOString().split('T')[0]}`];

                const response  = await Conta.valorMensal(params.join("&"));
                if (!this._isMounted) return;
                const data      = response.data;

                this.monthlyFinancialChartData = {
                    labels: data.labels,
                    datasets: [
                        {
                            type: 'bar',
                            label: 'Valor Total Pago', // Rótulo para as COLUNAS
                            backgroundColor: '#3B82F6',
                            data: data.pago, // Dados de "Total Pago" para as barras
                            yAxisID: 'y', // Aponta para o eixo Y principal
                        },
                        {
                            type: 'line',
                            label: 'Valor Total de Contas', // Rótulo para a LINHA
                            borderColor: '#EC4899',
                            borderWidth: 2,
                            fill: false,
                            data: data.total, // Dados de "Valor Total de Contas" para a linha
                            tension: 0.4,
                            yAxisID: 'y', // Aponta para o eixo Y principal
                        }
                    ]
                };
            } catch (error) {
                console.error("Erro ao carregar dados do gráfico financeiro mensal:", error);
                this.$toast.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar Gráfico Mensal', life: 5000 });
            } finally {
                this.isLoading.bar = false;
            }
        },

        formatMoney(valor) {
            if (!valor) return Mask.money(0);
            return Mask.money(valor);
        },
    },
};
</script>

<style scoped>
:deep(.p-card) {
    position: relative;
    overflow: hidden;
}
.chart-loader {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(26, 26, 26, 0.7);
    backdrop-filter: blur(2px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 10;
}
.chart-loader span {
    margin-top: 12px;
    font-size: 0.85rem;
    font-weight: 700;
    color: #f97316;
    letter-spacing: 0.05em;
}

/* ── Date Picker wrap ───────────────────────────────────── */
.date-picker-wrap {
    position: relative;
    display: flex;
    align-items: center;
}
.dp-cal-icon {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 2;
    color: #6b7280;
    display: flex;
}
.dp-cal-icon svg {
    width: 15px;
    height: 15px;
}

/* Input nativo do DatePicker */
.dp-input-wrapper :deep(input) {
    background: #1a1a1a !important;
    color: #e6edf3 !important;
    border: 1px solid #2a2a2a !important;
    border-radius: 8px !important;
    padding: 7px 12px 7px 36px !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    width: 148px;
    cursor: pointer;
    transition: border-color 160ms ease !important;
    caret-color: #f97316;
}
.dp-input-wrapper :deep(input)::placeholder {
    color: #6b7280;
}
.dp-input-wrapper :deep(input):focus {
    outline: none !important;
    border-color: #f97316 !important;
    box-shadow: 0 0 0 2px rgba(249,115,22,.18) !important;
}

/* Botão de ícone embutido do PrimeVue (removemos o nativo) */
:deep(.dp-root .p-datepicker-trigger) {
    display: none !important;
}

/* Painel flutuante */
:deep(.dp-panel),
:deep(.p-datepicker-panel) {
    background: #1e1e1e !important;
    border: 1px solid #2a2a2a !important;
    border-radius: 12px !important;
    box-shadow: 0 16px 48px rgba(0,0,0,.6) !important;
    overflow: hidden;
    margin-top: 6px;
    padding: 0 !important;
}

/* Header do calendário (mês/ano + setas) */
:deep(.p-datepicker-header) {
    background: #242424 !important;
    border-bottom: 1px solid #2a2a2a !important;
    padding: 10px 14px !important;
    display: flex;
    align-items: center;
    gap: 6px;
}
:deep(.p-datepicker-prev-button),
:deep(.p-datepicker-next-button) {
    background: transparent !important;
    border: 1px solid #2a2a2a !important;
    border-radius: 6px !important;
    color: #9ca3af !important;
    width: 28px !important;
    height: 28px !important;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 140ms ease, border-color 140ms ease !important;
}
:deep(.p-datepicker-prev-button:hover),
:deep(.p-datepicker-next-button:hover) {
    background: #2a2a2a !important;
    border-color: #f97316 !important;
    color: #f97316 !important;
}

/* Seletor de mês e ano */
:deep(.p-datepicker-select-month),
:deep(.p-datepicker-select-year) {
    background: transparent !important;
    border: none !important;
    color: #e6edf3 !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    cursor: pointer;
}
:deep(.p-datepicker-select-month:hover),
:deep(.p-datepicker-select-year:hover) {
    color: #f97316 !important;
}

/* Células dos meses (vista de mês) */
:deep(.p-datepicker-month) {
    border-radius: 6px !important;
    color: #d1d5db !important;
    transition: background 120ms ease !important;
    font-size: 13px !important;
    padding: 8px !important;
}
:deep(.p-datepicker-month:hover) {
    background: #2a2a2a !important;
    color: #fff !important;
}
:deep(.p-datepicker-month-selected) {
    background: #f97316 !important;
    color: #fff !important;
    font-weight: 700 !important;
}
</style>
